import React, { useContext, useEffect, useState } from 'react';
import { FormContext, FormSchemaFields } from '@sw-sw/lib-form';
import { UIControlType } from '@sw-sw/lib-form-control-types';
import { FormModal } from '../../Shared/form';
import { ProjectContext } from '../../../contexts/ProjectContext';
import schedulerApi from '../../../utils/api/inspectionSchedular';
import inspectionApi from '../../../utils/api/inspection';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import { useInspectionTypesByTemplate, defaultInspectionTypeOptions } from '../../../hooks/inspectionTypes';

interface AssignedForm {
  name_of_inspection: any;
  junction_id: number;
  template_id: number;
  template_name: string;
  inspection_types: string;
  inspector_id: number | null;
  inspector_name: string | null;
  active: boolean;
}

interface AssignedFormInspectionModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: () => void;
  projectId?: number;
}

const AssignedFormInspectionModal: React.FC<AssignedFormInspectionModalProps> = ({
  show,
  onClose,
  onSubmit,
  projectId,
}) => {
  const formContext = useContext(FormContext);
  const projectStore = useContext(ProjectContext);
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [assignedForms, setAssignedForms] = useState<AssignedForm[]>([]);
  const [selectedForm, setSelectedForm] = useState<AssignedForm | null>(null);

  // Fetch inspection types based on selected form's template_id
  const { 
    data: inspectionTypesData, 
    isLoading: isLoadingInspectionTypes 
  } = useInspectionTypesByTemplate(
    selectedForm?.template_id, 
    show && !!selectedForm?.template_id
  );

  // Fetch assigned forms from API
  const fetchAssignedForms = async () => {
    const currentProjectId = projectId || projectStore.project?.id;

    if (!currentProjectId) {
      console.log('Missing project ID - not making API call');
      return;
    }

    setIsLoading(true);
    try {
      const response = await schedulerApi.getProjectForms(currentProjectId);

      if (response.success) {
        // Filter only active forms
        const activeForms = response.data.filter((form: AssignedForm) => form.active);
        setAssignedForms(activeForms);

        if (activeForms.length === 0) {
          toast.warning('No active assigned forms found for this project');
        }
      } else {
        console.error('API call failed:', response);
        toast.error('Failed to load assigned forms');
      }
    } catch (error) {
      console.error('Error fetching assigned forms:', error);
      toast.error('Error loading assigned forms');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      fetchAssignedForms();
    } else {
      // Reset state when modal is closed
      setSelectedForm(null);
      setAssignedForms([]);
    }
  }, [show, projectId, projectStore.project?.id]);

  // Handle form selection change
  const handleFormChange = (junctionId: string) => {
    const form = assignedForms.find(f => f.junction_id.toString() === junctionId);
    setSelectedForm(form || null);

    // Clear inspection type when form changes since inspection types depend on template
    formContext.set('inspection_type', '');

    // Auto-populate inspector if available
    if (form && form.inspector_id) {
      formContext.set('inspector_id', form.inspector_id.toString());
    }
  };

  const handleSubmit = async () => {
    const formData = formContext.value;
    const currentProjectId = projectId || projectStore.project?.id;

    if (!currentProjectId) {
      toast.error('Project ID is required');
      return;
    }

    if (!formData.assigned_form) {
      toast.error('Please select an assigned form');
      return;
    }

    if (!formData.inspection_type) {
      toast.error('Please select an inspection type');
      return;
    }

    try {
      setIsLoading(true);

      const selectedFormData = assignedForms.find(
        f => f.junction_id.toString() === formData.assigned_form
      );

      if (!selectedFormData) {
        toast.error('Selected form not found');
        return;
      }

      // Use the same API as "Start Inspection" - POST to /api/inspections
      const requestBody = {
        projectId: currentProjectId,
        inspection_types: [formData.inspection_type],
        junctionId: selectedFormData.junction_id,
      };

      console.log('Creating inspection with:', requestBody);

      const data = await inspectionApi.createWithJunction(requestBody);

      if (data && data.id) {
        toast.success('Inspection created successfully');
        onSubmit();
        onClose();

        // Navigate to the newly created inspection's questionnaire
        history.push(`/inspection/${data.id}/questionnaire`);
      } else {
        console.error('Failed to create inspection: No inspection ID returned');
        toast.error('Failed to create inspection');
      }
    } catch (error) {
      console.error('Error creating inspection:', error);
      toast.error('Failed to create inspection. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Convert assigned forms to dropdown options
  const assignedFormOptions = assignedForms.map(form => ({
    value: form.junction_id.toString(),
    label: `${form.name_of_inspection}`,
  }));

  // Custom onChange handler
  const handleFormFieldChange = (fieldName: string, value: any) => {
    if (fieldName === 'assigned_form') {
      handleFormChange(value);
    }
    formContext.set(fieldName, value);
  };

  // Build inspection type options from API data, merging with defaults and removing duplicates
  const inspectionTypeOptions = (() => {
    const templateTypes = Array.isArray(inspectionTypesData?.inspectionTypes)
      ? inspectionTypesData.inspectionTypes
      : [];
    
    // Merge template types with defaults, removing duplicates
    const allTypes = Array.from(new Set([...templateTypes, ...defaultInspectionTypeOptions]));
    
    return allTypes.map((type: string, index: number) => ({
      id: index + 1,
      value: type,
    }));
  })();

  // Determine placeholder text for inspection type field
  const getInspectionTypePlaceholder = () => {
    if (!selectedForm) {
      return 'Select an assigned form first';
    }
    if (isLoadingInspectionTypes) {
      return 'Loading inspection types...';
    }
    if (inspectionTypeOptions.length === 0) {
      return 'No inspection types available';
    }
    return 'Select inspection type';
  };

  const schema = {
    assigned_form: {
      label: 'Assigned Form*',
      controlType: UIControlType.select,
      options: assignedFormOptions,
      validation: { required: true },
      placeholder: isLoading ? 'Loading assigned forms...' : 'Select an assigned form',
      disabled: isLoading || assignedForms.length === 0,
    },
    inspection_type: {
      label: 'Inspection Type*',
      controlType: UIControlType.select,
      validation: { required: true },
      options: inspectionTypeOptions,
      labelKey: 'value',
      valueKey: 'value',
      placeholder: getInspectionTypePlaceholder(),
      disabled: !selectedForm || isLoadingInspectionTypes || inspectionTypeOptions.length === 0,
    },
    inspector_name: {
      label: 'Inspector',
      controlType: UIControlType.text,
      placeholder: selectedForm?.inspector_name || 'Inspector from assigned form',
      disabled: true,
      description: 'Inspector is automatically assigned from the selected form',
    },
  };

  if (!show) return null;

  return (
    <FormModal
      modalProps={{
        title: 'New Inspection - Assigned Form',
        classes: 'assigned-form-inspection-modal',
      }}
      onSubmit={handleSubmit}
      onCancel={onClose}
      submitText="Create Inspection"
    >
      <div className="assigned-form-inspection-modal-content">
        {isLoading ? (
          <div className="loading-state">
            <p>Loading assigned forms...</p>
          </div>
        ) : assignedForms.length === 0 ? (
          <div className="no-forms-state">
            <p>No active assigned forms found for this project.</p>
            <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
              Please add and activate forms in the Inspection Settings section first.
            </p>
          </div>
        ) : (
          <FormSchemaFields
            schema={schema}
            formData={formContext.value}
            onChange={handleFormFieldChange}
          />
        )}
      </div>
    </FormModal>
  );
};

export default AssignedFormInspectionModal;
